import React, { useMemo } from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";

import { LineChart as ELineChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  AxisPointerComponent,
  TitleComponent,
  LegendComponent,
  DataZoomComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { useChartTheme, withChartTheme } from "./theme";

echarts.use([
  TitleComponent,
  TooltipComponent,
  AxisPointerComponent,
  LegendComponent,
  DataZoomComponent,
  GridComponent,
  ELineChart,
  CanvasRenderer,
]);

/**
 * LineChart
 * Props:
 *  - title, labels, series
 *  - unit?: string
 *  - yAxes?: [{ name, unit, color, position, min, max, integer, splitLine }]
 *  - yMinBuffer?: number | {bottom:number,top:number} | [bottom,top] | perAxisArray
 *      Examples:
 *        10                   -> top buffer 10 (legacy)
 *        {bottom:10, top:10}  -> bottom 10, top 10 for all axes
 *        [10,10]              -> shorthand bottom/top for single-axis
 *        [[5,10],[0,20]]      -> per-axis [bottom,top]
 *  - smooth, showLegend, showZoom, colors, height, theme
 *  - onPointClick, labelsPosition, labelRotate, labelFormatter, tooltipFormatter
 */
function LineChart(props) {
  const chartTheme = useChartTheme();
  const {
    title,
    labels = [],
    series = [],
    unit,
    yAxes = undefined,
    yMinBuffer = 5,
    smooth = true,
    showLegend = true,
    showZoom = true,
    colors = ["#5a6fa3", "#b5d56a", "#ff9f43", "#54a0ff", "#ee5253"],
    height = 320,
    theme,
    onPointClick,
    labelsPosition = "bottom",
    labelRotate = 0,
    labelFormatter = null,
    tooltipFormatter = null,
  } = props;

  const option = useMemo(() => {
    const hasArea = series.some((s) => !!s.area);

    // map series for echarts
    const echartsSeries = (series || []).map((s, idx) => {
      const color = colors[idx % colors.length];
      const yAxisIndex =
        typeof s.yAxisIndex === "number"
          ? s.yAxisIndex
          : s.axis === "right"
          ? 1
          : 0;

      return {
        name: s.name,
        type: s.type || "line",
        data: s.data,
        smooth: typeof s.smooth === "boolean" ? s.smooth : smooth,
        step: s.step,
        lineStyle: { width: 2, ...(s.lineStyle || {}) },
        symbol: s.symbol ?? "circle",
        showSymbol: typeof s.showSymbol === "boolean" ? s.showSymbol : false,
        symbolSize: s.symbolSize ?? 6,
        emphasis: s.emphasis ?? { focus: "series" },
        yAxisIndex,
        areaStyle: s.area
          ? {
              opacity: 0.18,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: echarts.color.modifyHSL(color, 0, 0, +0.1) },
                { offset: 1, color: echarts.color.modifyHSL(color, 0, 0, -0.8) },
              ]),
            }
          : undefined,
        itemStyle: { color },
      };
    });

    // normalize various yMinBuffer forms into {bottom, top} per-axis
    const normalizeBuffer = (raw) => {
      if (Array.isArray(raw)) {
        // shorthand [bottom,top] for single axis
        if (raw.length === 2 && typeof raw[0] === "number" && typeof raw[1] === "number") {
          return { bottom: Number(raw[0]) || 0, top: Number(raw[1]) || 0 };
        }
        // otherwise raw is per-axis array; return as-is
        return raw;
      }
      if (raw && typeof raw === "object") {
        return { bottom: Number(raw.bottom) || 0, top: Number(raw.top) || 0 };
      }
      if (typeof raw === "number") return { bottom: 0, top: raw };
      return { bottom: 0, top: 5 };
    };

    const bufferForAxis = (idx) => {
      const nb = normalizeBuffer(yMinBuffer);
      if (Array.isArray(nb)) {
        const val = nb[idx] !== undefined ? nb[idx] : nb[0];
        if (Array.isArray(val) && val.length === 2) {
          return { bottom: Number(val[0]) || 0, top: Number(val[1]) || 0 };
        }
        if (typeof val === "number") {
          return { bottom: 0, top: Number(val) || 0 };
        }
        if (val && typeof val === "object") {
          return { bottom: Number(val.bottom) || 0, top: Number(val.top) || 0 };
        }
        return { bottom: 0, top: 5 };
      }
      // nb is an object {bottom,top}
      return { bottom: Number(nb.bottom) || 0, top: Number(nb.top) || 0 };
    };

    const builtYAxes =
      Array.isArray(yAxes) && yAxes.length
        ? yAxes.map((ya, idx) => {
            const isIntegerAxis =
              ya?.integer === true ||
              /\b(count|alert|alerts|num|integer|items)\b/i.test(`${ya?.unit ?? ""} ${ya?.name ?? ""}`);

            const buf = bufferForAxis(idx);

            return {
              type: "value",
              name: ya?.name ?? "",
              position: ya?.position ?? (idx === 1 ? "right" : "left"),
              min:
                typeof ya?.min === "number"
                  ? ya.min
                  : (value) => {
                      if (!value || typeof value.min !== "number") return 0;
                      const rawMin = Math.floor(value.min - (Number.isFinite(buf.bottom) ? buf.bottom : 0));
                      return Number.isFinite(rawMin) ? Math.max(0, rawMin) : 0;
                    },
              max:
                typeof ya?.max === "number"
                  ? ya.max
                  : (value) => {
                      if (!value || typeof value.max !== "number") return undefined;
                      const v = Math.ceil(value.max + (Number.isFinite(buf.top) ? buf.top : 0));
                      return Number.isFinite(v) ? v : undefined;
                    },
              axisLine: { show: true, lineStyle: { color: ya?.color ?? "#ccc" } },
              axisTick: { show: true },
              minInterval: isIntegerAxis ? 1 : ya?.minInterval,
              axisLabel: {
                show: true,
                color: ya?.color ?? "#333",
                formatter: (v) => (isIntegerAxis ? String(Math.round(v)) : ya?.unit ? `${v} ${ya.unit}` : `${v}`),
              },
              splitLine: { show: ya?.splitLine ?? true, lineStyle: { color: "#eee" } },
            };
          })
        : [
            {
              type: "value",
              min: (value) => {
                if (!value || typeof value.min !== "number") return 0;
                const buf = bufferForAxis(0);
                const rawMin = Math.floor(value.min - (Number.isFinite(buf.bottom) ? buf.bottom : 0));
                return Number.isFinite(rawMin) ? Math.max(0, rawMin) : 0;
              },
              max: function (value) {
                if (!value || typeof value.max !== "number") return undefined;
                const buf = bufferForAxis(0);
                const v = Math.ceil(value.max + (Number.isFinite(buf.top) ? buf.top : 0));
                return Number.isFinite(v) ? v : undefined;
              },
              axisLine: { show: true, lineStyle: { color: "#ccc" } },
              axisTick: { show: true },
              axisLabel: { show: true, color: "#333", formatter: (v) => (unit ? `${v} ${unit}` : `${v}`) },
              splitLine: { show: true, lineStyle: { color: "#eee" } },
            },
          ];

    const tooltip = {
      trigger: "axis",
      appendToBody: true,
      confine: true,
      axisPointer: { type: "line" },
      formatter: tooltipFormatter
        ? tooltipFormatter
        : function (params) {
            if (!Array.isArray(params) || params.length === 0) return "";
            const xLabel = params[0]?.axisValueLabel ?? params[0]?.name ?? "";
            const rows = params.map((p) => {
              const val = p?.value ?? "";
              const suffix = unit ? ` ${unit}` : "";
              return `<div style="margin:2px 0">${p.marker} ${p.seriesName}: <strong>${val}${suffix}</strong></div>`;
            });
            return `<div style="min-width:160px"><div style="margin-bottom:6px;color:#666">${xLabel}</div>${rows.join("")}</div>`;
          },
    };

    return {
      color: colors,
      aria: { enabled: true },
      title: title ? { text: title, left: "left", textStyle: { fontSize: 13 } } : undefined,
      tooltip,
      legend: showLegend ? { top: 8, left: "center" } : undefined,
      grid: {
        left: 48,
        right: Array.isArray(yAxes) && yAxes.length > 1 ? 56 : 12,
        top: hasArea ? 48 : 40,
        bottom: showZoom ? 56 : 40,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: labels,
        position: labelsPosition,
        axisLabel: {
          rotate: labelRotate,
          formatter: typeof labelFormatter === "function" ? labelFormatter : labelFormatter ?? undefined,
          overflow: "truncate",
          interval: "auto",
          color: "#333",
        },
        axisLine: { show: true, lineStyle: { color: "#ccc" } },
        axisTick: { show: true, alignWithLabel: true },
      },
      yAxis: builtYAxes,
      dataZoom: showZoom
        ? [
            { type: "inside", start: 0, end: 100 },
            { type: "slider", start: 0, end: 100, bottom: 0 },
          ]
        : undefined,
      series: echartsSeries,
    };
  }, [
    title,
    labels,
    series,
    unit,
    yMinBuffer,
    yAxes,
    smooth,
    showLegend,
    showZoom,
    colors,
    labelsPosition,
    labelRotate,
    labelFormatter,
    tooltipFormatter,
  ]);

  const onEvents = useMemo(
    () => ({
      click: (params) => {
        if (onPointClick) onPointClick(params);
      },
    }),
    [onPointClick]
  );

  const themedOption = useMemo(

    () => withChartTheme(option, chartTheme),

    [option, chartTheme]

  );


  return (
    <ReactEChartsCore
      echarts={echarts}
      option={themedOption}
      notMerge={true}
      lazyUpdate={true}
      onEvents={onEvents}
      theme={theme}
      style={{ width: "100%", height: typeof height === "number" ? `${height}px` : height }}
      opts={{ renderer: "canvas" }}
    />
  );
}

export default LineChart;